import { splunkInstance } from '@/common/services/splunk';
import { CENTERS } from '@/common/types/centers';
import { getCookie } from 'cookies-next';
import dynamic from 'next/dynamic';
import Skeleton from '@/common/components/atom/skeleton/skeleton';
import Services from './services';
import pick from 'lodash/pick';
import BookingGlobalContextsProvider from '../../../../.plasmic/plasmic/paziresh_24_booking/PlasmicGlobalContextsProvider';
import { Fragment2 } from '@/common/fragment/fragment2';
import { removeHtmlTagInString } from '@/common/utils/removeHtmlTagInString';
import RismanAsideSection from './rismanAside';
import { HamdastKhedmatInactiveWrapper } from '@/modules/hamdast/components/khedmatInactiveWrapper';
import { withoutHamdastBimehnamaWidgets } from '@/modules/profile/utils/bikoInsuranceProfile';
import {
  hasCheraghFacilitiesDisplayData,
  withoutHamdastCheraghWidgets,
  type CenterFacilitiesMap,
} from '@/modules/profile/utils/cheraghFacilitiesProfile';
import type { SectionId } from '@/modules/profile/utils/sectionReadiness';

const RecommendWrapper = dynamic(() => import('./recommend'));
const PlasmicBookingAddressesWrapper = dynamic(() => import('.plasmic/plasmic/paziresh_24_booking/PlasmicBookingAddressesWrapper'));
const PlasmicBookingAddressesCard = dynamic(() => import('.plasmic/plasmic/paziresh_24_booking/PlasmicBookingAddressesCard'));
const FragmentRateReview = dynamic(
  () => import('./rateReview/fragmentRateReview').then(m => ({ default: m.FragmentRateReview })),
  {
    loading() {
      return <Skeleton w="100%" h="30rem" rounded="lg" />;
    },
  },
);
const Hamdast = dynamic(() => import('@/modules/hamdast/render').then(m => ({ default: m.Hamdast })), {
  loading() {
    return <Skeleton w="100%" h="4rem" rounded="lg" />;
  },
});
const OfficeFacilitiesWidget = dynamic(
  () =>
    import('@/modules/profile/components/officeFacilities/officeFacilitiesWidget').then(m => ({
      default: m.OfficeFacilitiesWidget,
    })),
  {
    loading() {
      return <Skeleton w="100%" h="4rem" rounded="lg" />;
    },
  },
);

/**
 * Progressive profile: load the aside's lazy chunks before its sections are revealed. A `dynamic()`
 * component renders nothing (or its loading box) on first mount and grows when the chunk lands —
 * after the section is already on screen, shifting everything below it (CLS). Once these chunks
 * are loaded, the component mounts at its real size before the frame is painted.
 */
let asideChunksLoaded = false;
/** True once the chunks were loaded in this page session (a revisit must not flash skeletons). */
export const areAsideChunksLoaded = () => asideChunksLoaded;

export const preloadAsideChunks = () =>
  // next/dynamic's `preload` is not reachable on the forwardRef it returns, and the dynamic() calls
  // must keep their inline `import()` (Next's loadable transform registers them for SSR hydration),
  // so the same modules are imported here; the dynamic components then resolve from webpack's cache.
  Promise.all([
    import('./recommend'),
    import('.plasmic/plasmic/paziresh_24_booking/PlasmicBookingAddressesWrapper'),
    import('.plasmic/plasmic/paziresh_24_booking/PlasmicBookingAddressesCard'),
    import('./rateReview/fragmentRateReview'),
    import('@/modules/profile/components/officeFacilities/officeFacilitiesWidget'),
  ]).then(() => {
    asideChunksLoaded = true;
  });

export const Aside = (data: any) => {
  const {
    information,
    centers,
    expertises,
    feedbacks,
    media,
    history,
    symptomes,
    similarLinks,
    customize,
    editable,
    handleViewAs,
    seo,
    isBulk,
    onlineVisit,
    waitingTimeInfo,
    fragmentComponents,
    hamdast,
    hamdastWidgetsData,
    hamdastWidgets,
    user_id,
    centerFacilities,
    progressivePending,
  } = data;

  const profileHamdastWidgets = withoutHamdastCheraghWidgets(withoutHamdastBimehnamaWidgets(hamdastWidgets));
  const facilitiesByCenter: CenterFacilitiesMap =
    centerFacilities && typeof centerFacilities === 'object' ? centerFacilities : {};

  const profileData = pick(data, [
    'information',
    'centers',
    'expertises',
    'feedbacks',
    'media',
    'waitingTimeInfo',
    'history',
    'symptomes',
    'seo',
    'onlineVisit',
    'user_id',
  ]);

  return [
    {
      sectionId: 'booking' as SectionId,
      id: 'book-me',
      isShow: centers?.length > 0,
      function: () => {
        return {
          id: information.id,
          doctor: information,
          expertises,
          onlineVisit,
          centers,
          waitingTimeInfo,
          slug: seo.slug,
          profileData,
          isBulk,
          progressivePending,
          preview: data.bookingPreview,
        };
      },
      children: (props: any) => (
        <Services
          {...props}
          enabledWidgets={profileHamdastWidgets}
          hamdastWidgetsData={hamdastWidgetsData}
          dontShowDeactiveBox={!!fragmentComponents?.risman}
        />
      ),
    },
    {
      sectionId: 'risman' as SectionId,
      id: 'risman',
      isShow: !customize.partnerKey && !!fragmentComponents?.risman,
      children: () => (
        <RismanAsideSection
          risman={fragmentComponents?.risman}
          slug={seo.slug}
          centers={centers ?? []}
          partnerKey={customize?.partnerKey}
        />
      ),
    },
    {
      sectionId: 'widgets' as SectionId,
      isShow: !customize?.partnerKey,
      noWrapper: true,
      children: () => {
        // When book-me/Services is shown, services-placement widgets render there (after online visit).
        const servicesOwnsWidgets = centers?.length > 0;
        if (servicesOwnsWidgets) return null;

        return profileHamdastWidgets
          ?.filter((widget: any) => widget?.placement?.includes?.('services'))
          ?.map((widget: any) => (
            <Hamdast
              key={widget.id}
              id={widget.id}
              app={widget?.app}
              backendData={hamdastWidgetsData?.[widget.id] ?? undefined}
              profileData={profileData}
              widgetData={{
                placement: widget?.placement,
                placement_metadata: widget.placements_metadata,
              }}
            />
          ));
      },
    },
    // Recommend
    {
      sectionId: 'recommend' as SectionId,
      isShow: information?.should_recommend_other_doctors && centers?.[0] && expertises?.expertises?.[0],
      function: () => {
        return {
          doctorId: information.id,
          city: { name: centers[0].city, en_slug: information.city_en_slug },
          groupExpertise: expertises.group_expertises[0],
          expertises: expertises.expertises,
          clickRecommendEvent: (doctor: any, elementName?: string, elementContent?: string) => {
            splunkInstance('doctor-profile').sendEvent({
              group: 'recommend',
              type: 'clickrecommend',
              event: {
                data: {
                  terminal_id: getCookie('terminal_id'),
                  user_agent: window.navigator.userAgent,
                  page_url: window.location.pathname,
                  referrer: document.referrer,
                  group_expertises: expertises.group_expertises?.[0]?.name ?? 'سایر',
                  doctor_name: information.display_name,
                  centers_types: centers.map((center: any) => center.center_type_name),
                  server_id: information.server_id,
                  recommendations: doctor,
                  element_name: elementName,
                  element_content: elementContent,
                },
              },
            });
          },
        };
      },
      children: (props: any) => <RecommendWrapper {...props} />,
      dataAttributes: { 'data-nosnippet': 'true' },
    },
    // Reviews
    {
      sectionId: 'reviews' as SectionId,
      id: 'reviews',
      isShow: customize.showRateAndReviews && fragmentComponents?.raviComponentTopOrderProfile,
      // Mobile-only copy; off unless the top-order flag is on — no skeleton for it while pending.
      pendingSkeleton: !!fragmentComponents?.raviComponentTopOrderProfile,
      children: (props: any) => (
        <div className="md:hidden flex flex-col gap-y-3">
          <FragmentRateReview profileData={profileData} />
        </div>
      ),
    },
    // Centers Info
    {
      sectionId: 'centers' as SectionId,
      id: 'phone-and-address',
      isShow: centers?.some?.((center: any) => center.id !== CENTERS.CONSULT),
      function: () => {
        return {
          centers: centers
            .filter((center: any) => center.id !== CENTERS.CONSULT)
            .map((center: any) => ({
              id: center.id,
              userCenterId: center.user_center_id,
              address: center.address,
              city: center.city,
              slug: center.center_type === 1 ? `/dr/${seo.slug}` : `/center/${center.slug}`,
              description: removeHtmlTagInString(center.description ?? '').trim(),
              phoneNumbers: center?.display_number_array,
              name: center.center_type !== 1 ? center.name : `مطب ${information?.display_name}`,
              location: center.map,
            })),
          onEventPhoneNumber: (centerId: any) => {
            const center = centers.find((center: any) => center.id === centerId);
            splunkInstance('doctor-profile').sendEvent({
              group: 'doctor profile',
              type: 'see center phone',
              event: {
                data: {
                  doctor: {
                    full_name: information.display_name,
                    server_id: information.server_id,
                    slug: seo.slug,
                    id: information.id,
                    expertise: expertises?.expertises?.[0]?.alias_title,
                    group_expertise: expertises.group_expertises[0].name,
                  },
                  center: {
                    center_status: center?.status,
                    center_type: center?.center_type,
                    center_name: center?.name,
                    city_en_slug: center?.city_en_slug,
                  },
                  user: {
                    terminal_id: getCookie('terminal_id'),
                  },
                },
              },
            });
          },
          onEventAddress: (centerId: any) => {
            const center = centers.find((center: any) => center.id === centerId);
            splunkInstance('doctor-profile').sendEvent({
              group: 'doctor profile',
              type: 'see center map',
              event: {
                data: {
                  doctor: {
                    full_name: information.display_name,
                    server_id: information.server_id,
                    slug: seo.slug,
                    id: information.id,
                    expertise: expertises?.expertises?.[0]?.alias_title,
                    group_expertise: expertises.group_expertises[0].name,
                  },
                  center: {
                    center_status: center?.status,
                    center_type: center?.center_type,
                    center_name: center?.name,
                    city_en_slug: center?.city_en_slug,
                  },
                  user: {
                    terminal_id: getCookie('terminal_id'),
                  },
                },
              },
            });
          },
        };
      },
      children: (props: any) => (
        <BookingGlobalContextsProvider>
          <Fragment2
            name="AddressesWrapper"
            Component={PlasmicBookingAddressesWrapper}
            args={{
              ...profileData,
              slug: seo.slug,
              // Remove "گزارش اشتباه در اطلاعات مرکز" action in header.
              button: { render: () => null } as any,
              children: centers
                .filter((center: any) => center.id !== CENTERS.CONSULT)
                .map((center: any) => (
                  <Fragment2
                    key={center.id}
                    name="AddressesCard"
                    Component={PlasmicBookingAddressesCard}
                    args={{
                      ...profileData,
                      title: center.name,
                      map: center.map,
                      centerId: center.id,
                      address: center.address,
                      city: center.city,
                      displayNumberArray: center.display_number_array,
                      slug: center.slug,
                      centerType: center.center_type == 1 ? 'office' : 'hospital',
                      description: removeHtmlTagInString(center.description ?? '').trim(),
                      userCenterId: center.user_center_id,
                      centerName: center.name,
                      children: (() => {
                        const centerFacilityData = facilitiesByCenter[String(center.id)];
                        const showBikoCheragh = hasCheraghFacilitiesDisplayData(centerFacilityData);
                        const centerWidgets = profileHamdastWidgets.filter(
                          (widget: any) =>
                            widget?.placement?.includes?.('center_info') &&
                            widget.placements_metadata?.center_info?.center_ids?.includes?.(center.id),
                        );
                        const hasKhedmat = centerWidgets.some(
                          (widget: any) => widget?.app === 'khedmat' || widget?.id === 'rrr3pbcc8ixyi8p',
                        );
                        return (
                          <div className="flex flex-col w-full gap-2">
                            {showBikoCheragh && centerFacilityData && (
                              <OfficeFacilitiesWidget
                                key={`biko-cheragh-${center.id}`}
                                data={centerFacilityData}
                                centerId={center.id}
                                doctorName={`${information?.name ?? ''} ${information?.family ?? ''}`.trim()}
                                profileSlug={seo?.slug}
                              />
                            )}
                            {centerWidgets.map((widget: any) => (
                              <Hamdast
                                key={widget.id}
                                id={widget.id}
                                app={widget?.app}
                                backendData={hamdastWidgetsData?.[widget.id] ?? undefined}
                                profileData={profileData}
                                widgetData={{
                                  placement: widget?.placement,
                                  placement_metadata: widget.placements_metadata,
                                  center_id: center.id,
                                }}
                              />
                            ))}
                            {!hasKhedmat && (
                              <HamdastKhedmatInactiveWrapper
                                profileData={profileData}
                                centerId={center.id}
                                userId={user_id}
                              />
                            )}
                          </div>
                        );
                      })(),
                    }}
                  />
                )),
            }}
          />
        </BookingGlobalContextsProvider>
      ),
    },
    {
      sectionId: 'widgets' as SectionId,
      isShow: !customize?.partnerKey,
      noWrapper: true,
      children: () =>
        profileHamdastWidgets
          ?.filter((widget: any) => widget?.placement?.includes?.('sidebar'))
          ?.map((widget: any) => (
            <Hamdast
              key={widget.id}
              id={widget.id}
              app={widget?.app}
              backendData={hamdastWidgetsData?.[widget.id] ?? undefined}
              profileData={profileData}
              widgetData={{
                placement: widget?.placement,
                placement_metadata: widget.placements_metadata,
              }}
            />
          )),
    },
  ];
};

